import type { ReactNode } from 'react';
import React, { createContext, useContext, useState } from 'react';

export type SaveStatus =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'saved' }
  | { status: 'error'; message: string };

interface SaveStatusContextType {
  saveStatus: SaveStatus;
  setSaveStatus: (status: SaveStatus) => void;
  markSaving: () => void;
  markSaved: () => void;
  markError: (message: string) => void;
}

const SaveStatusContext = createContext<SaveStatusContextType | undefined>(undefined);

export function SaveStatusProvider({ children }: { children: ReactNode }) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ status: 'idle' });

  const markSaving = () => {
    setSaveStatus({ status: 'saving' });
  };

  const markSaved = () => {
    setSaveStatus({ status: 'saved' });
    // Auto-reset to idle after 2 seconds
    setTimeout(() => {
      setSaveStatus({ status: 'idle' });
    }, 2000);
  };

  const markError = (message: string) => {
    setSaveStatus({ status: 'error', message });
    // Auto-reset to idle after 5 seconds
    setTimeout(() => {
      setSaveStatus({ status: 'idle' });
    }, 5000);
  };

  return (
    <SaveStatusContext.Provider value={{ saveStatus, setSaveStatus, markSaving, markSaved, markError }}>
      {children}
    </SaveStatusContext.Provider>
  );
}

export function useSaveStatus() {
  const context = useContext(SaveStatusContext);
  if (!context) {
    throw new Error('useSaveStatus must be used within SaveStatusProvider');
  }
  return context;
}
